import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  DEFAULT_DATE_PREFS,
  setDateDisplayPrefs,
  type TimeNotationPref,
} from '@core/utils/datePrefs';

/**
 * Persisted UI preferences.
 *
 * `sidebarOpen` was component state on `MainLayoutComponent`, so it reset to expanded
 * on every navigation that remounted the layout.
 *
 * `vendorColumns` fixes a real annoyance: `dashboard.component.ts` held `columnConfigs`
 * in memory only, so a user's column selection was lost on every refresh. Persisting it
 * is a behaviour improvement recorded in BEHAVIOUR-CHANGES.md.
 */
export interface ColumnConfig {
  id: string;
  label: string;
  selected: boolean;
}

interface UiPrefsState {
  sidebarOpen: boolean;
  settingsExpanded: boolean;
  /** Column selection for the Business Partners "All" tab. `null` = use defaults. */
  vendorColumns: ColumnConfig[] | null;

  /**
   * ============================================================================
   * Which partners have had the AECB report waived, by vendor id.
   * ============================================================================
   *
   * "Emirati Customer / Foreign Customer / AECB Report is Not Available" is the one answer that makes the
   * AECB document optional, and it was component state on the Business Partner form — so a user
   * ticked it, saved the draft, reopened it and was blocked on the AECB report again, with the box
   * unticked and no sign it had ever been answered.
   *
   * ⚠️ IT IS HERE BECAUSE THE RECORD HAS NOWHERE TO PUT IT. The vendor contract carries
   * `is_aecb_report_available` inside the scoring block, but that field is POLICY rather than input
   * — "a report is always needed", see `CreditScoringSection` — and writing a waiver into it would
   * flip an answer the product deliberately fixed. Nothing else in the payload describes the
   * customer as Emirati or foreign.
   *
   * ⚠️ SO THIS IS PER-BROWSER, AND THAT IS A REAL LIMIT, NOT A DETAIL: a colleague reopening the
   * same draft does not see the waiver and is asked for the document again. It fixes the case that
   * was reported — the same person resuming their own draft — and it is the same interim shape as
   * the display preferences above, which also outlived a missing endpoint. A field on the record is
   * the real fix and needs the backend.
   *
   * Only waived records are stored; clearing one removes the key rather than writing `false`, so the
   * map stays the size of the exceptions instead of growing a row per partner ever opened.
   */
  aecbWaivers: Record<string, boolean>;

  /**
   * My Profile display preferences.
   *
   * ⚠️ INTERIM, per Q1: Angular's `saveProfile()` is a TODO no-op and there is no persistence
   * endpoint, so these live per-browser. `datePrefs.ts` explains how they reach `formatDate`, and why
   * the switch to a server endpoint will touch only this store's hydration.
   */
  timeNotation: TimeNotationPref;

  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setSettingsExpanded: (expanded: boolean) => void;
  setVendorColumns: (columns: ColumnConfig[]) => void;
  setAecbWaiver: (vendorId: string, waived: boolean) => void;
  resetVendorColumns: () => void;
  setDatePrefs: (prefs: { timeNotation: TimeNotationPref }) => void;
}

export const useUiPrefsStore = create<UiPrefsState>()(
  persist(
    (set) => ({
      sidebarOpen: true,
      settingsExpanded: false,
      vendorColumns: null,
      aecbWaivers: {},
      timeNotation: DEFAULT_DATE_PREFS.timeNotation,

      setAecbWaiver: (vendorId, waived) =>
        set((state) => {
          const next = { ...state.aecbWaivers };
          if (waived) next[vendorId] = true;
          else delete next[vendorId];
          return { aecbWaivers: next };
        }),

      setDatePrefs: ({ timeNotation }) => {
        // Pushed into the module holder immediately, so a date rendered in the same tick is correct.
        setDateDisplayPrefs({ timeNotation });
        set({ timeNotation });
      },

      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
      setSettingsExpanded: (settingsExpanded) => set({ settingsExpanded }),
      setVendorColumns: (vendorColumns) => set({ vendorColumns }),
      resetVendorColumns: () => set({ vendorColumns: null }),
    }),
    {
      name: 'vm.uiPrefs',
      storage: createJSONStorage(() => localStorage),
      /**
       * Push the REHYDRATED preference into the module holder.
       *
       * Without this the choice applies only for the session in which it was made: `setDatePrefs`
       * writes the holder on change, but a page reload restores the store from localStorage without
       * calling any setter, so dates would silently revert to the defaults.
       */
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        /*
         * ⚠️ A STORED `dateFormat` IS DELIBERATELY NOT READ. The date format is fixed at
         * DD-MM-YYYY, so a browser that once chose one of the old options switches over on its
         * next load.
         * The stale key is left in localStorage and simply ignored; clearing it would be a
         * migration for no benefit, since nothing reads it.
         */
        setDateDisplayPrefs({
          timeNotation: state.timeNotation ?? DEFAULT_DATE_PREFS.timeNotation,
        });
      },
    },
  ),
);
